/**
 * Uitgebreide dienstbeschrijvingen voor de offerte-PDF.
 * Geschreven in de Rinqel tone-of-voice: duidelijk, persoonlijk, zonder jargon.
 */

export interface OfferteDienstInfo {
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
}

export const offerteDiensten: Record<string, OfferteDienstInfo> = {
  /* ── Werkplek ── */
  "wp-cloud-only": {
    title: "Managed Cloud Only Werkplek",
    subtitle: "Werken vanuit de cloud — overal, op elk apparaat",
    description:
      "Met de Cloud Only Werkplek werk je volledig vanuit de browser of mobiele app. Je krijgt zakelijke e-mail, 1 TB OneDrive-opslag, Microsoft Teams en SharePoint — zonder dat je software op je pc hoeft te installeren. Wij beheren alles: van beveiligingsbeleid tot back-ups. Jij werkt, wij zorgen dat het werkt.",
    highlights: [
      "Zakelijke e-mail met Exchange Online (50 GB)",
      "1 TB OneDrive-opslag per gebruiker",
      "Microsoft Teams voor chatten, bellen en videobellen",
      "SharePoint Online voor documenten en samenwerking",
      "Inclusief dagelijkse back-up en monitoring",
      "Volledig beheerd door Rinqel — geen omkijken naar",
    ],
  },
  "wp-managed": {
    title: "Managed Werkplek",
    subtitle: "De complete werkplek — alles geregeld, niets vergeten",
    description:
      "De Managed Werkplek is ons meest complete pakket. Je krijgt alles van de Cloud Only werkplek, plus de volledige Office-apps (Word, Excel, PowerPoint, Outlook) geïnstalleerd op je apparaat. Daarnaast beveiligen we je apparaten met Microsoft Defender en Intune. Of je nu op kantoor werkt, thuis of onderweg — je werkplek is altijd veilig, snel en up-to-date.",
    highlights: [
      "Alles van Cloud Only, plus desktop Office-apps",
      "Microsoft Defender for Business voor geavanceerde beveiliging",
      "Apparaatbeheer via Microsoft Intune",
      "Automatische updates en patchbeheer",
      "Conditional Access: alleen veilige apparaten krijgen toegang",
      "Inclusief dagelijkse back-up, monitoring en support",
    ],
  },
  "wp-kiosk": {
    title: "Managed Kiosk Werkplek",
    subtitle: "Voor gedeelde apparaten — simpel, veilig, vergrendeld",
    description:
      "De Kiosk Werkplek is ontworpen voor apparaten die door meerdere medewerkers worden gebruikt, zoals op de winkelvloer, in het magazijn of in de zorg. Het apparaat wordt vergrendeld op alleen de apps die nodig zijn. Geen toegang tot persoonlijke bestanden, geen risico op ongewenste wijzigingen. Maximaal veilig, minimaal gedoe.",
    highlights: [
      "Vergrendeld op alleen de benodigde apps",
      "Geschikt voor gedeeld gebruik door meerdere medewerkers",
      "Ideaal voor retail, zorg, logistiek en horeca",
      "Beheerd en beveiligd via Microsoft Intune",
      "Inclusief monitoring en support",
    ],
  },
  "wp-mobile-device": {
    title: "Managed Mobile Device Only",
    subtitle: "Zakelijk beheer voor tablets en telefoons",
    description:
      "Voor medewerkers die alleen werken met een tablet of smartphone. We beveiligen het apparaat, zorgen dat zakelijke apps beschikbaar zijn en houden alles up-to-date. Op basis van Microsoft 365 F3, inclusief Exchange, Teams en OneDrive — speciaal afgestemd op mobiele medewerkers.",
    highlights: [
      "Zakelijke e-mail, Teams en OneDrive op mobiel",
      "Apparaatbeheer en beveiliging via Intune",
      "Geschikt voor buitendienstmedewerkers en monteurs",
      "Automatische app-distributie en updates",
      "Inclusief back-up en support",
    ],
  },
  "wp-mailbox": {
    title: "Losse Mailbox (Exchange Online)",
    subtitle: "Professioneel mailen met je eigen domeinnaam",
    description:
      "Een zakelijke mailbox op basis van Exchange Online. 50 GB opslagruimte, agenda, contactpersonen en overal toegankelijk via de browser of je favoriete mail-app. Inclusief spamfilter, back-up en support. Ideaal als je alleen e-mail nodig hebt, zonder de volledige werkplek.",
    highlights: [
      "50 GB Exchange Online mailbox",
      "Agenda en contactpersonen inbegrepen",
      "Toegankelijk via webmail, Outlook of mobiele app",
      "Inclusief spamfilter en anti-phishing",
      "Dagelijkse back-up door Rinqel",
    ],
  },
  "wp-shared-mailbox": {
    title: "Gedeelde Mailbox",
    subtitle: "Eén adres, meerdere collega's",
    description:
      "Perfect voor adressen als info@, verkoop@ of administratie@. Meerdere collega's hebben toegang tot dezelfde mailbox, zonder extra licentiekosten. E-mails worden centraal beheerd en iedereen ziet wie wat heeft beantwoord.",
    highlights: [
      "Gedeeld e-mailadres voor het hele team",
      "Geen extra licentie per gebruiker nodig",
      "Iedereen ziet de volledige mailhistorie",
      "Inclusief back-up en support",
    ],
  },

  /* ── Optioneel ── */
  "opt-keeper": {
    title: "Keeper Wachtwoordmanager",
    subtitle: "Nooit meer wachtwoorden rondsturen",
    description:
      "Keeper is een professionele wachtwoordmanager voor je hele organisatie. Elke medewerker krijgt een eigen beveiligde kluis. Teamwachtwoorden deel je veilig via gedeelde mappen. Inloggen via Single Sign-On met Microsoft, automatisch invullen via de browserextensie en volledige audit-trail. Veilige on- en offboarding van medewerkers in een paar klikken.",
    highlights: [
      "Persoonlijke kluis per medewerker",
      "Gedeelde mappen voor teamwachtwoorden",
      "Single Sign-On met Microsoft (SSO)",
      "Automatisch wachtwoorden invullen",
      "Volledige audit-trail: wie heeft wat bekeken",
      "Veilige on- en offboarding",
    ],
  },
  "opt-signatures": {
    title: "Centrale E-mailhandtekeningen",
    subtitle: "Professionele, uniforme handtekeningen voor iedereen",
    description:
      "Alle uitgaande e-mails krijgen automatisch een professionele, uniforme handtekening. Centraal beheerd, zodat je altijd zeker weet dat iedereen de juiste contactgegevens, logo's en disclaimers gebruikt. Geen gedoe met handmatig instellen per medewerker.",
    highlights: [
      "Automatisch toegepast op alle uitgaande e-mails",
      "Centraal beheerd — één aanpassing, overal doorgevoerd",
      "Inclusief logo, contactgegevens en disclaimers",
      "Consistent professioneel naar klanten en partners",
    ],
  },

  /* ── Internet ── */
  "inet-vdsl": {
    title: "Rinqel VDSL",
    subtitle: "Betrouwbaar zakelijk internet via de telefoonlijn",
    description:
      "Rinqel VDSL biedt snelheden tot 200 Mbit download en 60 Mbit upload. We plaatsen altijd onze eigen router, zodat wij maximale controle hebben en jij minimale storingen. Inclusief vast IP-adres, professionele monitoring en persoonlijke support. Ideaal voor locaties waar glasvezel nog niet beschikbaar is.",
    highlights: [
      "Tot 200 Mbit download, 60 Mbit upload",
      "Onze eigen router voor maximale controle",
      "Vast IP-adres inbegrepen",
      "Proactieve monitoring op storingen",
      "Persoonlijke support bij haperingen",
    ],
  },
  "inet-glasvezel": {
    title: "Rinqel Glasvezel",
    subtitle: "De snelste verbinding voor veeleisende bedrijven",
    description:
      "Glasvezel biedt symmetrische snelheden tot 4000 Mbit — upload is gelijk aan download. Ideaal voor bedrijven die veel data versturen, videobellen of in de cloud werken. Met vast IP, eigen router en proactieve monitoring weet je zeker dat je altijd online bent.",
    highlights: [
      "Symmetrisch: upload = download",
      "Snelheden tot 4000 Mbit",
      "Vast IP-adres en eigen router",
      "Proactieve monitoring en persoonlijke support",
      "Ideaal voor cloud, video en grote bestanden",
    ],
  },
  "inet-starlink": {
    title: "Rinqel met Starlink",
    subtitle: "Internet overal — ook waar geen kabel komt",
    description:
      "Met Starlink-satellietinternet ben je overal online, ook op locaties waar glasvezel of VDSL niet beschikbaar is. Snelheden tot 305 Mbit download en 40 Mbit upload. Ideaal voor bouwlocaties, evenementen, schepen of afgelegen bedrijventerreinen.",
    highlights: [
      "Tot 305 Mbit download, 40 Mbit upload",
      "Werkt overal — geen kabel nodig",
      "Snel te installeren op elke locatie",
      "Inclusief monitoring en support",
    ],
  },

  /* ── Netwerk ── */
  "net-switch": {
    title: "Managed Switch",
    subtitle: "Je netwerk professioneel verbonden",
    description:
      "Een beheerde netwerkswitch verbindt al je apparaten via kabels met het netwerk. Wij monitoren de switch, voeren firmware-updates uit en grijpen proactief in bij problemen. Geen omkijken naar, gewoon een netwerk dat werkt.",
    highlights: [
      "Professionele netwerkswitch met monitoring",
      "Automatische firmware-updates",
      "Proactief onderhoud en storingsdetectie",
      "Inclusief in je maandelijkse beheerprijs",
    ],
  },
  "net-ap": {
    title: "Managed Access Point (WiFi)",
    subtitle: "Overal sterk WiFi-signaal",
    description:
      "Onze beheerde access points zorgen voor betrouwbaar WiFi in je hele pand. Met gastnetwerk, automatische updates en continue monitoring heb je altijd een stabiel draadloos netwerk — zonder er zelf naar om te kijken.",
    highlights: [
      "Betrouwbaar WiFi in het hele pand",
      "Apart gastnetwerk inbegrepen",
      "Automatische updates en monitoring",
      "Professioneel beheer door Rinqel",
    ],
  },

  /* ── Telefonie ── */
  "tel-centrale": {
    title: "VoIP Centrale",
    subtitle: "Complete telefooncentrale in de cloud",
    description:
      "Met de Rinqel VoIP Centrale heb je een volwaardige telefooncentrale zonder hardware. Bellers worden automatisch doorverbonden, je stelt wachtrijen in, gebruikt een keuzemenu en beluistert voicemails via e-mail. Alles beheerd via een overzichtelijk online portaal.",
    highlights: [
      "Geen hardware nodig — alles in de cloud",
      "Keuzemenu, wachtrijen en doorschakelen",
      "Voicemail-naar-e-mail",
      "Online beheerportaal",
      "Makkelijk schaalbaar",
    ],
  },
  "tel-voip": {
    title: "VoIP Aansluiting",
    subtitle: "Bellen via internet — professioneel en flexibel",
    description:
      "Elke VoIP Aansluiting is een telefoonlijn voor een medewerker of toestel. Bel met je zakelijke nummer via een bureautelefoon, laptop of mobiel. Inclusief doorschakelen, voicemail en nummerherkenning.",
    highlights: [
      "Eén aansluiting per medewerker of toestel",
      "Bellen via bureautelefoon, laptop of mobiel",
      "Doorschakelen, voicemail en nummerherkenning",
      "Direct koppelbaar met Microsoft Teams",
    ],
  },
  "tel-teams": {
    title: "Bellen met Teams",
    subtitle: "Je zakelijke nummer rechtstreeks in Microsoft Teams",
    description:
      "Met Bellen met Teams gebruik je je zakelijke telefoonnummer direct vanuit Microsoft Teams. Geen aparte telefoon-app nodig — bel en gebeld worden vanuit dezelfde omgeving waar je ook chat en videobelt. Ideaal voor hybride werken.",
    highlights: [
      "Bellen en gebeld worden vanuit Teams",
      "Je zakelijke nummer, overal beschikbaar",
      "Geen extra app nodig",
      "Ideaal voor thuiswerken en hybride werken",
    ],
  },
  "tel-vamo": {
    title: "VAMO — Vast-Mobiel Integratie",
    subtitle: "Je vaste nummer, ook op je mobiel",
    description:
      "Met VAMO bel je op je mobiele telefoon met je vaste zakelijke nummer. Klanten zien altijd je zakelijke nummer, ook als je onderweg bent. Inkomende gesprekken op je vaste nummer komen ook op je mobiel binnen. Altijd bereikbaar, altijd professioneel.",
    highlights: [
      "Bellen met je vaste nummer op je mobiel",
      "Inkomende gesprekken op beide apparaten",
      "Klanten zien altijd je zakelijke nummer",
      "Ideaal voor buitendienstmedewerkers",
    ],
  },
  "tel-crm": {
    title: "CRM & ERP Koppeling",
    subtitle: "Direct klantgegevens bij inkomende gesprekken",
    description:
      "Koppel je telefonie aan je CRM of ERP-systeem. Bij een inkomend gesprek zie je direct de klantgegevens op je scherm. Na afloop wordt het gesprek automatisch gelogd. Scheelt tijd, voorkomt fouten en maakt je team professioneler.",
    highlights: [
      "Automatische klantherkenning bij inkomende gesprekken",
      "Gesprekken automatisch gelogd in je CRM",
      "Koppelbaar met de meeste CRM- en ERP-systemen",
      "Verhoogt productiviteit en klanttevredenheid",
    ],
  },
};

/**
 * Get dienst info for a given configurator item ID.
 * Returns undefined if no description is available.
 */
export function getDienstInfo(id: string): OfferteDienstInfo | undefined {
  return offerteDiensten[id];
}
